import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, teamMembers } from '@/mocks/testimonials';

const AboutPage = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9srbfdlbrt1qcgaj8ng', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试。');
          setFormStatus('error');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试。');
      setFormStatus('error');
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '深度解析',
        url: 'https://www.example.com/',
        description: '以学术视角深度剖析都市文化现象，提供客观、全面、有深度的情感与社会议题分析。',
        foundingDate: '2024',
        founders: [
          { '@type': 'Person', name: '陈思远' },
        ],
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 深度解析',
        url: 'https://www.example.com/about',
        isPartOf: { '@type': 'WebSite', name: '深度解析', url: 'https://www.example.com/' },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Page Header */}
        <section className="w-full py-12 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-6 mb-3">
              关于我们
            </h1>
            <p className="text-foreground-600 leading-relaxed">
              以学术视角深度剖析都市文化现象，为公众提供理解复杂情感议题的知识性窗口。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              品牌故事
            </h2>
            <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
              <p>
                <strong>深度解析</strong>成立于2024年，由一群关注当代都市文化与社会变迁的研究者、媒体人和心理咨询师共同发起。我们的初衷很简单：在信息碎片化和情绪化表达泛滥的今天，为公众提供一个理解复杂情感与社会现象的理性空间。
              </p>
              <p>
                "人妻猎人"现象是我们关注的第一个深度选题。我们注意到，这一标签在网络上引发了广泛讨论，但大多数讨论停留在道德评判或猎奇层面，缺乏系统性的学术梳理和客观分析。于是，我们决定从社会学、心理学和伦理学等多个学科视角，对这一现象进行全方位剖析。
              </p>
              <p>
                我们的内容团队由社会学博士、临床心理学硕士和资深媒体人组成。每篇文章都经过严格的事实核查和多轮编辑审校，确保内容的学术严谨性和大众可读性之间的平衡。我们不追求点击量驱动的标题党，只提供有价值、有深度的分析。
              </p>
              <p>
                我们相信，理解复杂的社会现象是解决问题的第一步。无论是婚姻危机的预防、情感出轨的成因解读，还是对当代婚姻制度面临的挑战的思考，我们都希望通过客观、深入的讨论，帮助读者建立更清晰的认知框架。
              </p>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-600 font-medium mt-0.5 mb-3">
                    {member.role}
                  </p>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                    ))}
                  </div>
                  <blockquote className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{t.content}"
                  </blockquote>
                  <div className="border-t border-background-100 pt-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-600">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。
              </p>
            </div>

            {formStatus === 'success' ? (
              <div className="py-6 px-6 bg-primary-50 border border-primary-200 rounded-lg text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-check-line text-xl text-primary-600"></i>
                </div>
                <p className="text-primary-700 font-medium text-sm">
                  感谢您的留言！我们会尽快回复。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                    留言 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="spam-field"
                />
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
              </form>
            )}
            {formStatus === 'error' && (
              <p className="text-xs text-red-600 mt-2 text-center">{formError}</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default AboutPage;